import { memo, type ReactNode, useState } from 'react';
import cn from 'classnames';
import { format } from 'date-fns';
import type { ChatAttachment, ChatMessage } from '../../model/types';
import {
  downloadChatAttachment,
  getChatAttachmentPreviewUrl,
  openChatAttachment,
} from '../../lib/attachmentLinks';
import { IconClock, IconDownload, IconFile, IconMessageRead, IconMessageSent, IconPlay } from '../icons';
import { MessageActionMenu } from '../MessageActionMenu/MessageActionMenu';
import {
  getMessageQuoteAuthorName,
  MessageQuotePreview,
} from '../MessageQuotePreview/MessageQuotePreview';

const URL_SPLIT_REGEX = /(https?:\/\/\S+)/g;
const isUrl = (value: string) => /^https?:\/\/\S+$/.test(value);

const renderTextWithLinks = (text: string): ReactNode[] =>
  text.split(URL_SPLIT_REGEX).map((part, index) =>
    isUrl(part) ? (
      <a
        key={`link-${index}-${part}`}
        href={part}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="break-all text-[#2937B5] underline underline-offset-2"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );

export type MessageBubbleProps = {
  message: ChatMessage;
  onRetry?: (message: ChatMessage) => void | Promise<void>;
  onOpenAttachment?: (attachment: ChatAttachment) => void;
  onReply?: (message: ChatMessage) => void;
  onTogglePin?: (message: ChatMessage) => void;
  onForward?: (message: ChatMessage) => void;
  onReport?: (message: ChatMessage) => void;
  showAuthorName?: boolean;
  isPinned?: boolean;
  isHighlighted?: boolean;
  onReplyPreviewClick?: (messageId: number) => void;
};

function stopEventPropagation(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}

function OutgoingStatus({
  pending,
  failed,
  status,
}: {
  pending?: boolean;
  failed?: boolean;
  status?: 'sent' | 'read';
}) {
  if (pending) {
    return <IconClock className="h-3.5 w-6.5 text-default-400" />;
  }
  if (failed) {
    return <span className="text-[11px] font-semibold text-danger">!</span>;
  }
  if (status === 'read') {
    return (
      <IconMessageRead className="h-3.5 w-auto shrink-0 text-[#1E1E1E] aspect-[31/16]" />
    );
  }
  if (status === 'sent') {
    return <IconMessageSent className="h-3.5 w-auto shrink-0 text-default-400 aspect-[31/16]" />;
  }
  return null;
}

function formatAttachmentSize(size: number): string {
  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }
  if (size >= 1024) {
    return `${Math.ceil(size / 1024)} KB`;
  }
  return `${size} B`;
}

const VideoPreview = memo(
  ({
    attachment,
    onOpen,
  }: {
    attachment: ChatAttachment;
    onOpen?: (attachment: ChatAttachment) => void;
  }) => {
  const [thumbnailFailed, setThumbnailFailed] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const thumbnailUrl = !thumbnailFailed ? attachment.thumbnailUrl : null;
  // Кадр рисуется только из локального файла неотправленного сообщения; видео с сервера
  // ссылку не несёт — её запрашивает просмотрщик при открытии
  const localVideoUrl = attachment.isLocal ? attachment.url : null;

  return (
    <button
      type="button"
      className="relative mb-1 block w-full overflow-hidden rounded-xl bg-black/90 text-left"
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.(attachment);
      }}
    >
      <div className="aspect-video w-full">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={attachment.name}
            className="h-full w-full object-cover"
            onError={() => setThumbnailFailed(true)}
          />
        ) : localVideoUrl && !videoFailed ? (
          <video
            src={localVideoUrl}
            preload="auto"
            muted
            playsInline
            tabIndex={-1}
            className="pointer-events-none h-full w-full object-cover"
            onError={() => setVideoFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-black/90" />
        )}
      </div>
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-default-800 shadow-md">
          <IconPlay className="ml-0.5" />
        </div>
      </div>
    </button>
  );
});
VideoPreview.displayName = 'VideoPreview';

function AttachmentActions({
  attachment,
  className,
}: {
  attachment: ChatAttachment;
  className?: string;
}) {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <button
        type="button"
        className="text-[11px] font-medium text-inherit underline-offset-2 hover:underline"
        onClick={(event) => {
          stopEventPropagation(event);
          openChatAttachment(attachment);
        }}
      >
        Open
      </button>
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-black/10 transition-colors hover:bg-black/15"
        aria-label={`Download ${attachment.name}`}
        onClick={(event) => {
          stopEventPropagation(event);
          downloadChatAttachment(attachment);
        }}
      >
        <IconDownload className="h-4 w-4" />
      </button>
    </div>
  );
}

function AttachmentBlock({
  attachment,
  isOwn,
  onOpenAttachment,
}: {
  attachment: ChatAttachment;
  isOwn: boolean;
  onOpenAttachment?: (attachment: ChatAttachment) => void;
}) {
  if (attachment.kind === 'image') {
    return (
      <div className="mb-2">
        <button
          type="button"
          className="block w-full text-left"
          onClick={(event) => {
            event.stopPropagation();
            onOpenAttachment?.(attachment);
          }}
        >
          <img
            src={getChatAttachmentPreviewUrl(attachment) ?? undefined}
            alt={attachment.name}
            className="block max-h-64 w-auto max-w-full rounded-xl object-contain transition-opacity hover:opacity-95"
          />
        </button>
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-tiny font-medium opacity-90">{attachment.name}</p>
          <AttachmentActions attachment={attachment} />
        </div>
      </div>
    );
  }

  if (attachment.kind === 'video') {
    return (
      <div className="mb-2">
        <VideoPreview attachment={attachment} onOpen={onOpenAttachment} />
        <div className="mt-2 flex items-center justify-between gap-3">
          <p className="min-w-0 flex-1 truncate text-tiny font-medium opacity-90">{attachment.name}</p>
          <AttachmentActions attachment={attachment} />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 flex items-center gap-3 rounded-xl bg-black/[0.03] px-3 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            isOwn ? 'bg-white/55' : 'bg-default-200',
          )}
        >
          <IconFile className={cn(isOwn ? 'text-default-700' : 'text-default-600')} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium">{attachment.name}</p>
          <p className="text-tiny opacity-80">{formatAttachmentSize(attachment.size)}</p>
        </div>
      </div>
      <AttachmentActions attachment={attachment} className="shrink-0" />
    </div>
  );
}

export const MessageBubble = memo(
  ({
    message,
    onRetry,
    onOpenAttachment,
    onReply,
    onTogglePin,
    onForward,
    onReport,
    showAuthorName = false,
    isPinned = false,
    isHighlighted = false,
    onReplyPreviewClick,
  }: MessageBubbleProps) => {
    if (message.kind === 'system') {
      return (
        <div className="flex w-full justify-center py-1">
          <div className="inline-flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-[16px] bg-[#ECECEC] px-[8px] py-[4px] shadow-[0_0_8px_rgba(0,0,0,0.08)]">
            <p className="text-center text-[14px] font-normal leading-[120%] tracking-[0] text-[#1E1E1E]">
              {message.text ?? ''}
            </p>
          </div>
        </div>
      );
    }

    const isOwn = message.isOwn;
    const time = format(message.sentAt, 'HH:mm');

    const shell = (body: ReactNode, extraWide?: boolean) => (
      <div
        className={cn(
          'max-w-[min(100%,480px)] text-small shadow-sm',
          isOwn
            ? 'rounded-2xl rounded-br-none bg-[#E9EFFF] p-2.5 text-foreground'
            : 'rounded-2xl rounded-bl-md bg-content1 px-3.5 py-2.5 text-foreground',
          isHighlighted && 'ring-2 ring-primary/30 shadow-[0_0_0_4px_rgba(87,76,255,0.10)]',
          extraWide && 'max-w-[min(100%,520px)]',
        )}
      >
        {body}
      </div>
    );

    const footer = () => (
      <div
        className={cn(
          'mt-1 flex items-center justify-end gap-1',
          isOwn ? 'text-default-500' : 'text-default-400',
        )}
      >
        <span className="text-[11px] tabular-nums">{time}</span>
        {isOwn ? (
          <OutgoingStatus
            pending={message.pending}
            failed={message.failed}
            status={message.outgoingStatus}
          />
        ) : null}
      </div>
    );

    const hasWideMedia = message.attachments.some(
      (attachment) => attachment.kind === 'image' || attachment.kind === 'video',
    );
    const shouldShowAuthorName = showAuthorName && !isOwn && message.kind === 'user';

    const inner: ReactNode = (
      <>
        {shouldShowAuthorName ? (
          <p className="mb-1.5 text-tiny font-semibold text-primary">{message.authorName}</p>
        ) : null}

        {message.replyTo ? (
          <MessageQuotePreview
            authorName={getMessageQuoteAuthorName(message.replyTo.sender)}
            text={message.replyTo.text}
            attachments={message.replyTo.attachments}
            isOwn={isOwn}
            onClick={
              onReplyPreviewClick ? () => onReplyPreviewClick(message.replyTo!.id) : undefined
            }
          />
        ) : null}

        {message.attachments.map((attachment) => (
          <AttachmentBlock
            key={attachment.key}
            attachment={attachment}
            isOwn={isOwn}
            onOpenAttachment={
              attachment.kind === 'image' || attachment.kind === 'video'
                ? onOpenAttachment
                : undefined
            }
          />
        ))}

        {message.text ? (
          <p className="whitespace-pre-wrap break-words leading-relaxed">
            {renderTextWithLinks(message.text)}
          </p>
        ) : null}

        {message.failed ? (
          <div className="mt-1 flex items-center justify-between gap-3">
            <p className="text-[11px] font-medium text-danger">Failed to send</p>
            {onRetry ? (
              <button
                type="button"
                className="text-[11px] font-semibold text-danger transition-opacity hover:opacity-80"
                onClick={() => {
                  void onRetry(message);
                }}
              >
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        {footer()}
      </>
    );

    const bubble = shell(inner, hasWideMedia);
    const canOpenActions =
      message.kind === 'user' && !!(onReply || onTogglePin || onForward || onReport);

    return (
      <div className={cn('flex w-full', isOwn ? 'justify-end' : 'justify-start')}>
        {canOpenActions ? (
          <MessageActionMenu
            timestampLabel={time}
            isPinned={isPinned}
            onReply={onReply ? () => onReply(message) : undefined}
            onTogglePin={onTogglePin ? () => onTogglePin(message) : undefined}
            onForward={onForward ? () => onForward(message) : undefined}
            onReport={onReport ? () => onReport(message) : undefined}
          >
            {bubble}
          </MessageActionMenu>
        ) : (
          bubble
        )}
      </div>
    );
  },
);

MessageBubble.displayName = 'MessageBubble';
